import { createSlice } from "@reduxjs/toolkit";

export const ORDER_FILTER_KEYS = [
  "Status",
  "lensGroup",
  "prescriptionType",
  "LensMountType",
  "prescriptionMethod",
  "CaptifyVersion",
  "orderSheet",
  "ShopifyItemID",
];

export const hasAdvancedOrderFilters = (filterState) => {
  if (!filterState || typeof filterState !== "object") return false;
  return Object.entries(filterState).some(
    ([key, value]) =>
      ORDER_FILTER_KEYS.includes(key) &&
      value !== undefined &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0)
  );
};

const filterSlice = createSlice({
  name: "filters",
  initialState: {
    search: {},
    activeTab: "all",
    status: "",
    page: 1,
    filterPanelOpen: false,
    /** Bumped by Socket.IO order events to refetch the list without resetting filters/page */
    realtimeVersion: 0,
    /** Order keys (ItemID / ShopifyItemID / ShopifyId) marked unseen from socket until opened */
    realtimeUnseenIds: [],
    /** Keys cleared after markOrderSeen — wins over stale list cache until refetch */
    realtimeSeenIds: [],
    /**
     * Last successful orders list response for the current filters/page.
     * Used to paint instantly when returning from order details.
     */
    ordersListCache: null,
  },
  reducers: {
    bumpOrdersRealtime: (state) => {
      state.realtimeVersion = (state.realtimeVersion || 0) + 1;
    },
    setOrdersListCache: (state, action) => {
      const { queryKey, payload } = action.payload || {};
      if (!queryKey) {
        state.ordersListCache = null;
        return;
      }
      state.ordersListCache = {
        queryKey,
        payload: payload ?? null,
        savedAt: Date.now(),
      };
    },
    clearOrdersListCache: (state) => {
      state.ordersListCache = null;
    },
    markOrdersRealtimeUnseen: (state, action) => {
      const ids = (Array.isArray(action.payload) ? action.payload : [action.payload])
        .filter((id) => id !== undefined && id !== null && id !== "")
        .map((id) => String(id));
      if (!ids.length) return;
      const existing = new Set(state.realtimeUnseenIds || []);
      ids.forEach((id) => existing.add(id));
      state.realtimeUnseenIds = Array.from(existing);
      // Opening/creating again as unseen should clear any prior local-seen flag for those keys
      const removeSeen = new Set(ids);
      state.realtimeSeenIds = (state.realtimeSeenIds || []).filter(
        (id) => !removeSeen.has(String(id))
      );
    },
    clearOrdersRealtimeUnseen: (state, action) => {
      const ids = (Array.isArray(action.payload) ? action.payload : [action.payload])
        .filter((id) => id !== undefined && id !== null && id !== "")
        .map((id) => String(id));
      if (!ids.length) return;
      const remove = new Set(ids);
      state.realtimeUnseenIds = (state.realtimeUnseenIds || []).filter(
        (id) => !remove.has(String(id))
      );
    },
    markOrdersLocallySeen: (state, action) => {
      const ids = (Array.isArray(action.payload) ? action.payload : [action.payload])
        .filter((id) => id !== undefined && id !== null && id !== "")
        .map((id) => String(id));
      if (!ids.length) return;
      const seen = new Set(state.realtimeSeenIds || []);
      ids.forEach((id) => seen.add(id));
      state.realtimeSeenIds = Array.from(seen);
      const remove = new Set(ids);
      state.realtimeUnseenIds = (state.realtimeUnseenIds || []).filter(
        (id) => !remove.has(String(id))
      );
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.page = 1;
    },
    updateOrdersFilterSearch: (state, action) => {
      if (!state.search || typeof state.search !== "object") {
        state.search = {};
      }

      const updates = action.payload ?? {};
      Object.entries(updates).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          delete state.search[key];
        } else {
          state.search[key] = value;
        }
      });

      state.page = 1;
    },
    setOrdersTextSearch: (state, action) => {
      if (!state.search || typeof state.search !== "object") {
        state.search = {};
      }
      const text = action.payload;
      if (text) {
        state.search.search = text;
      } else {
        delete state.search.search;
      }
      state.page = 1;
    },
    setActiveTab: (state, action) => {
      if (state.activeTab !== action.payload) {
        state.page = 1;
      }
      state.activeTab = action.payload;
    },
    setFilterPanelOpen: (state, action) => {
      state.filterPanelOpen = action.payload;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
      state.page = 1;
    },
    setHighlightedIds: (state, action) => {
      state.highlightedIds = action.payload; // array of IDs
    },
    setPage: (state, action) => {
      state.page = action.payload ?? 1;
    },
    resetSearch: (state) => {
      state.search = {};
      state.page = 1;
      state.ordersListCache = null;
    },
    resetFilters: (state) => {
      state.search = {};
      state.activeTab = "all";
      state.status = "";
      state.page = 1;
      state.filterPanelOpen = false;
      state.ordersListCache = null;
    },
    clearHighlightedIds: (state) => {
      state.highlightedIds = [];
    },
    clearHighlightedShopifyIds: (state) => {
      state.highlightedShopifyIds = [];
    },
    setHighlightedShopifyIds: (state, action) => {
      state.highlightedShopifyIds = action.payload; // array of IDs
    },
  },
});

export const {
  setSearch,
  updateOrdersFilterSearch,
  setOrdersTextSearch,
  setActiveTab,
  setFilterPanelOpen,
  setStatus,
  setPage,
  resetFilters,
  setHighlightedIds,
  clearHighlightedIds,
  setHighlightedShopifyIds,
  clearHighlightedShopifyIds,
  bumpOrdersRealtime,
  markOrdersRealtimeUnseen,
  clearOrdersRealtimeUnseen,
  markOrdersLocallySeen,
  setOrdersListCache,
  clearOrdersListCache,
} = filterSlice.actions;
export default filterSlice.reducer;
